import React from "react";
import type { BatchCase } from "../../hooks/useBatchUpload";

const UPLOADED = new Set(["uploaded", "analyzing", "ready", "failed"]);

const rowTint = (s: BatchCase["status"]) =>
  s === "upload-failed" ? "text-warn" : UPLOADED.has(s) ? "text-signal" : "text-fg/60";

/** Per-study upload progress + an aggregate "N of M uploaded" bar. */
export const BatchUploadPanel: React.FC<{ cases: BatchCase[] }> = ({ cases }) => {
  const uploaded = cases.filter((c) => UPLOADED.has(c.status)).length;
  const failed = cases.filter((c) => c.status === "upload-failed").length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
        <span data-testid="batch-upload-count">
          {uploaded} of {cases.length} uploaded
        </span>
        {failed > 0 && <span className="text-warn">{failed} failed</span>}
      </div>
      <div className="h-1 w-full overflow-hidden rounded bg-border">
        <div
          className="h-full bg-signal transition-all"
          style={{ width: `${cases.length ? (uploaded / cases.length) * 100 : 0}%` }}
        />
      </div>
      <div className="max-h-72 overflow-y-auto rounded border border-border custom-scrollbar">
        {cases.map((c) => {
          const isFailed = c.status === "upload-failed";
          const isDone = UPLOADED.has(c.status);
          // Bytes are fully sent (100%) but the server hasn't confirmed yet — the
          // request is still in flight while the receiver ingests + writes to S3.
          // Show "finalizing" rather than a static 100% that reads as done.
          const finalizing = c.status === "uploading" && c.progress >= 100;
          // Failed/done fill the bar; uploading tracks live %; pending is empty.
          const pct = isFailed || isDone ? 100 : c.status === "uploading" ? c.progress : 0;
          const label = isFailed
            ? "FAILED"
            : isDone
              ? "✓"
              : finalizing
                ? "FINALIZING…"
                : c.status === "uploading"
                  ? `${c.progress}%`
                  : "QUEUED";
          return (
            <div
              key={c.studyInstanceUid}
              className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0"
            >
              <span className="w-40 flex-none truncate font-mono text-[12px] text-fg/80">
                …{c.studyInstanceUid.slice(-16)}
              </span>
              <div className="h-1 flex-1 overflow-hidden rounded bg-border">
                <div
                  className={`h-full transition-all ${isFailed ? "bg-warn" : "bg-signal"} ${finalizing ? "animate-pulse" : ""}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span
                className={`flex-none text-right font-mono text-[10px] tracking-[0.06em] ${finalizing ? "w-24" : "w-16"} ${rowTint(c.status)}`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
